import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Header from './components/Header';
import { MarketDataProvider, useMarketData } from './contexts/MarketDataContext';
import { ValorePriceProvider } from './contexts/ValorePriceContext';
import { BybitPriceProvider } from './contexts/BybitPriceContext';
import { UnifiedPriceProvider, useUnifiedPrices } from './contexts/UnifiedPriceContext';
import CryptoHoldings from './components/CryptoHoldings';
import SpotTradingForms from './components/SpotTradingForms';
import CFDTradingForms from './components/CFDTradingForms';
import FuturesTradingForms from './components/FuturesTradingForms';
import FuturesMyOrders from './components/FuturesMyOrders';
import TradingChart from './components/TradingChart';
import Markets from './components/Markets';
import SpotMyOrders from './components/SpotMyOrders';
import ArbitrageRobotPage from './components/ArbitrageRobotPage';
import PolymarketEventBetting from './components/PolymarketEventBetting';
import CryptoWithdrawalModal from './components/CryptoWithdrawalModal';
import PropFirmChallengePage from './components/PropFirmChallengePage';
import WalletPage from './components/WalletPage';
import ProfilePage from './components/ProfilePage';
import OrderBook from './components/OrderBook';
import PairDetailsPanel from './components/PairDetailsPanel';
import TradingTerminalLayout from './components/TradingTerminalLayout';
import TradingPairHeader from './components/TradingPairHeader';
import ChartToolbar from './components/ChartToolbar';
import OrderBookWithTrades from './components/OrderBookWithTrades';
import UnifiedTradingPanel from './components/UnifiedTradingPanel';
import PositionsPanel from './components/PositionsPanel';
import TakeProfitStopLossModal from './components/TakeProfitStopLossModal';
import StakingPage from './pages/StakingPage';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import TradingFeesPage from './pages/TradingFeesPage';
import FinnhubWebSocketTest from './components/FinnhubWebSocketTest';
import StockIndexTestPage from './components/StockIndexTestPage';
import ValoreTestPage from './pages/ValoreTestPage';
import LoadingScreen from './components/LoadingScreen';
import MarketLoadingScreen from './components/MarketLoadingScreen';
import AuthModal from './components/AuthModal';
import { supabase } from './lib/supabaseClient';
import { useAuth } from './hooks/useAuth';
import { useDatabase } from './hooks/useDatabase';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from './constants/tradingPairs';

const isCryptoSymbol = (symbol: string): boolean => {
  return TOP_CRYPTO_PAIRS.some(pair => pair.symbol === symbol);
};

const isCfdSymbol = (symbol: string): boolean => {
  return CFD_INSTRUMENTS.some(instrument => instrument.symbol === symbol);
};
import { getUserCfdTier, getInstrumentTypeFromSymbol } from './constants/tradingTiers';
import { useUserAssets } from './hooks/useUserAssets';
import { useFuturesTrading } from './hooks/useFuturesTrading';
import { usePropFirmTrading } from './hooks/usePropFirmTrading';
import { useWalletBreakdown } from './hooks/useWalletBreakdown';
import { useUserLeverage } from './hooks/useUserLeverage';
import SwapCryptoPage from './components/SwapCryptoPage';
import SpinTheWheel from './components/SpinTheWheel';
import PaymentSandbox from './components/PaymentSandbox';
import MobileTradingLayout from './components/mobile/MobileTradingLayout';
import { UserHeartbeat } from './components/UserHeartbeat';
import ExchangesTradingPage from './components/ExchangesTradingPage';
import ExchangesArbitragePage from './components/ExchangesArbitragePage';
import { DisplayCurrency, isDisplayCurrency } from './utils/displayCurrency';
import { getTradingModeFromPath, TradingMode } from './routes/dashboardRoutes';

export type { TradingMode } from './routes/dashboardRoutes';

const DEFAULT_PAIR_BY_MODE: Partial<Record<TradingMode, string>> = {
  futures: 'BTCUSDT',
  exchanges: 'BTCUSDT',
  cfd: 'EUR/USD',
};

export interface FuturesPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  liquidationPrice: number;
  unrealizedPnl: number;
  margin: number;
  roi: number;
  created_at: string;
  user_email?: string;
}

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade' | 'robot_profit' | 'binary_trade' | 'stake' | 'staking_profit' | 'staking_return' | 'challenge_fee' | 'challenge_reward';
  amount: number;
  description: string;
  status: 'completed' | 'pending' | 'failed';
  timestamp: string;
  created_at?: string;
}

// User status tiers based on portfolio value in USD
export type UserStatus = 'No-Coiner' | 'Shrimp' | 'Crab' | 'Octopus' | 'Dolphin' | 'Shark' | 'Whale' | 'Humpback';

function AppContent() {
  const location = useLocation();
  const { user, loading: authLoading, signOut } = useAuth();
  const [selectedDisplayCurrency, setSelectedDisplayCurrency] = useState<DisplayCurrency>('USD');
  const { marketData } = useMarketData();
  const { getPriceBySymbol, isConnected: valoreConnected, lastUpdateTime } = useUnifiedPrices();
  const {
    balances,
    updateBalances,
    fetchBalances,
    robotState,
    updateRobotState,
    loading: dbLoading,
    transactions,
    addTransaction,
    fetchTransactions,
    userStakes,
    addUserStake,
    eventOutcomes,
    eventBets,
    events,
    addEventBet,
    newsItems,
    portfolioSnapshots,
    createPortfolioSnapshot,
    fetchPortfolioSnapshots,
    kycStatus,
    updateKycStatus,
    referralCode,
    referralCount,
    referredUsers,
    fetchUserStakes,
    calculateCurrentEarnings,
    cancelUserStake,
    isDemoAccount
  } = useDatabase();

  useEffect(() => {
    let cancelled = false;

    const loadDisplayCurrency = async () => {
      if (!user?.id) {
        setSelectedDisplayCurrency('USD');
        return;
      }

      const { data, error } = await supabase
        .from('users')
        .select('display_currency')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Failed to load display currency:', error);
        return;
      }

      if (!cancelled && isDisplayCurrency(data?.display_currency)) {
        setSelectedDisplayCurrency(data.display_currency);
      }
    };

    void loadDisplayCurrency();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleDisplayCurrencyChange = useCallback(async (currency: DisplayCurrency) => {
    setSelectedDisplayCurrency(currency);

    if (!user?.id) return;

    const { error } = await supabase
      .from('users')
      .update({ display_currency: currency })
      .eq('id', user.id);

    if (error) {
      console.error('Failed to save display currency:', error);
    }
  }, [user?.id]);
  const { assets, fetchAssets, updateAssetBalance } = useUserAssets();
  const {
    activePositions,
    openOrders,
    fetchActivePositions,
    fetchOpenOrders,
    openPosition,
    closePosition,
    cancelOrder,
    cancelAllOpenOrders,
    calculateLiquidationPrice,
    positionHistory,
    orderHistory,
    loadPositionHistory,
    loadOrderHistory,
    updateStopLossTakeProfit
  } = useFuturesTrading();
  
  const {
    propPositions,
    propOrders,
    propPositionHistory,
    propChallengeAccount,
    fetchPropPositions,
    fetchPropOrders,
    fetchPropPositionHistory,
    fetchPropChallengeAccount,
    initializeChallenge,
    cancelChallenge,
    placePropOrder,
    closePropPosition,
    cancelPropOrder,
    cancelAllPropOrders,
    closeAllPropPositions,
    loading: propFirmLoading
  } = usePropFirmTrading();
  
  // Wallet breakdown for margin tracking
  const {
    totalBalance,
    usedMargin,
    unrealizedPnl,
    availableBalance,
    reserved,
    refreshBreakdown
  } = useWalletBreakdown();

  // Calculate actual available balance for trading
  const actualAvailableBalance = useMemo(() => {
    return Math.max(0, availableBalance);
  }, [availableBalance]);
  
  const tradingMode = getTradingModeFromPath(location.pathname);
  const [selectedPair, setSelectedPair] = useState(
    () => DEFAULT_PAIR_BY_MODE[tradingMode] ?? 'BTCUSDT'
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreparingMarkets, setIsPreparingMarkets] = useState(false);

  const [editingTPSL, setEditingTPSL] = useState<{
    positionId: string;
    type: 'takeProfit' | 'stopLoss';
    position: any;
  } | null>(null);
  
  useEffect(() => {
    const defaultPair = DEFAULT_PAIR_BY_MODE[tradingMode];

    if (defaultPair) {
      setSelectedPair(defaultPair);
    }
  }, [tradingMode]);

  // Interval reference for position refresh
  const positionRefreshIntervalRef = useRef<number | null>(null);
  
  // Check if this is a password recovery link
  const isRecoveryLink = useMemo(() => {
    const hash = window.location.hash;
    if (!hash) return false;
    
    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    return type === 'recovery' && !!accessToken;
  }, []);

  // Add this function inside App component, before the return
const handleUpdatePassword = async (newPassword: string) => {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to update password:", err);
    return false;
  }
};


  // Handle auth callback from CRM impersonation
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');

      if (accessToken) {
        try {
          console.log('Auth callback detected, handling CRM impersonation...');

          // Sign out current session first
          await supabase.auth.signOut();

          // Set the new session with the provided tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || accessToken // Use access token as fallback if no refresh token
          });

          if (error) {
            console.error('Error setting session from auth callback:', error);
            throw error;
          }

          console.log('Successfully set session from auth callback:', data);

          // Clear the URL parameters to clean up the address bar
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);

        } catch (error) {
          console.error('Error handling auth callback:', error);
        }
      }
    };

    handleAuthCallback();
  }, []);

  // Detect when user logs in and show market loading screen
  useEffect(() => {
    if (user && !authLoading && !isRecoveryLink) {
      const hasShownLoading = sessionStorage.getItem('marketLoadingShown');

      if (!hasShownLoading) {
        setIsPreparingMarkets(true);
        sessionStorage.setItem('marketLoadingShown', 'true');
      }
    }
  }, [user, authLoading, isRecoveryLink]);


  // Collect all active symbols for WebSocket subscription
  const activeSymbols = useMemo(() => {
    // Always ensure we have at least default symbols to prevent empty array
    const neededSymbols = new Set<string>(['BTCUSDT', 'ETHUSDT']); // Default symbols
    
    // Always include the selected pair (most important)
    if (selectedPair) {
      neededSymbols.add(selectedPair);
    }
    
    // Include symbols with active positions
    activePositions.forEach(p => {
      if (p.symbol) neededSymbols.add(p.symbol);
    });
    
    // Include symbols with prop positions
    propPositions.forEach(p => {
      if (p.symbol) neededSymbols.add(p.symbol);
    });
    
    // Convert to array with selectedPair first
    const symbolsArray = Array.from(neededSymbols);
    const finalSymbols = selectedPair ? 
      [selectedPair, ...symbolsArray.filter(s => s !== selectedPair)] : 
      symbolsArray;
    
    // Limit to 10 symbols max for better coverage
    const limitedSymbols = finalSymbols.slice(0, 10);
    
    return limitedSymbols;
  }, [selectedPair, activePositions, propPositions]);
  
  // Always enable polling as fallback regardless of WebSocket status

  
  const currentSelectedPairPrice = useMemo(() => {
    const price = getPriceBySymbol(selectedPair);
    if (price > 0) return price;
    const marketPrice = (marketData || []).find(data => data.symbol === selectedPair)?.price;
    return marketPrice || 0;
  }, [selectedPair, marketData, getPriceBySymbol, lastUpdateTime]);

  const getCurrentPrice = useCallback((symbol: string): number => {
    const price = getPriceBySymbol(symbol);
    if (price > 0) return price;
    const dbData = (marketData || []).find(data => data.symbol === symbol);
    return dbData?.price || 0;
  }, [marketData, getPriceBySymbol, lastUpdateTime]);

  // Get current BTC price from combined data
  const currentBtcPrice = getCurrentPrice('BTCUSDT');

  // Get current price for selected pair
  // Calculate total portfolio value
 const totalPortfolioValue = useMemo(() => {
  let total = balances.usdt_balance; // Start with USDT balance

  // Get the current BTCUSDT price specifically
  const btcUsdtPrice = (marketData || []).find(data => data.symbol === 'BTCUSDT')?.price ||
                       0;

  total += balances.btc_balance * btcUsdtPrice; // Add BTC value

  // Add value of other user assets
  const allAssets = assets || [];
  if (allAssets.length > 0) {
    allAssets.forEach(asset => {
    // Skip USDT and BTC as they are handled above
    if (asset.asset_symbol !== 'USDT' && asset.asset_symbol !== 'BTC' && marketData) {
      // Construct the USDT pair symbol (e.g., 'ETHUSDT')
      const assetUsdtSymbol = `${asset.asset_symbol}USDT`;

      // Get the price from marketData
      const assetPrice = (marketData || []).find(data => data.symbol === assetUsdtSymbol)?.price ||
                         0;

      total += asset.balance * assetPrice;
    }
    });
  }

  return total;
}, [balances, assets, marketData]);

  // Function to determine user status based on portfolio value in USD
  const getUserStatus = useCallback((portfolioValue: number): UserStatus => {
    if (portfolioValue < 250) return 'No-Coiner';
    if (portfolioValue < 10000) return 'Shrimp';
    if (portfolioValue < 25000) return 'Crab';
    if (portfolioValue < 50000) return 'Octopus';
    if (portfolioValue < 100000) return 'Dolphin';
    if (portfolioValue < 500000) return 'Shark';
    if (portfolioValue < 2000000) return 'Whale';
    return 'Humpback';
  }, []);

  // Get user status
  const userStatus = getUserStatus(totalPortfolioValue);

  // Calculate user's CFD tier based on portfolio value
  const userCfdTier = useMemo(() => {
    return getUserCfdTier(totalPortfolioValue);
  }, [totalPortfolioValue]);

  const userLeverageSettings = useUserLeverage(user?.id, totalPortfolioValue);

  const currentMaxForexLeverage = useMemo(() => userLeverageSettings.maxForex, [userLeverageSettings]);
  const currentMaxCommoditiesLeverage = useMemo(() => userLeverageSettings.maxCommodities, [userLeverageSettings]);
  const currentMaxStocksLeverage = useMemo(() => userLeverageSettings.maxStocks, [userLeverageSettings]);
  const currentMaxFuturesLeverage = useMemo(() => userLeverageSettings.maxFutures, [userLeverageSettings]);
  const currentMinForexLeverage = useMemo(() => userLeverageSettings.minForex, [userLeverageSettings]);
  const currentMinCommoditiesLeverage = useMemo(() => userLeverageSettings.minCommodities, [userLeverageSettings]);
  const currentMinStocksLeverage = useMemo(() => userLeverageSettings.minStocks, [userLeverageSettings]);
  const currentMinFuturesLeverage = useMemo(() => userLeverageSettings.minFutures, [userLeverageSettings]);

  const currentCfdMaxLeverage = useMemo(() => {
    if (!isCfdSymbol(selectedPair)) return currentMaxStocksLeverage;
    const instType = getInstrumentTypeFromSymbol(selectedPair);
    if (instType === 'forex') return currentMaxForexLeverage;
    if (instType === 'commodity') return currentMaxCommoditiesLeverage;
    return currentMaxStocksLeverage;
  }, [selectedPair, currentMaxForexLeverage, currentMaxCommoditiesLeverage, currentMaxStocksLeverage]);

  const currentCfdMinLeverage = useMemo(() => {
    if (!isCfdSymbol(selectedPair)) return currentMinStocksLeverage;
    const instType = getInstrumentTypeFromSymbol(selectedPair);
    if (instType === 'forex') return currentMinForexLeverage;
    if (instType === 'commodity') return currentMinCommoditiesLeverage;
    return currentMinStocksLeverage;
  }, [selectedPair, currentMinForexLeverage, currentMinCommoditiesLeverage, currentMinStocksLeverage]);

  // Initialize app data - only when user is authenticated
  useEffect(() => {
    // Skip initialization if this is a recovery link
    if (isRecoveryLink) {
      setIsLoading(false);
      return;
    }
    
    const initializeData = async () => {
      // Only fetch user-specific data if user is authenticated
      if (user) {
        await fetchTransactions();
        await fetchAssets();
        await fetchPortfolioSnapshots();
        await fetchUserStakes();
        await fetchActivePositions();
        await fetchOpenOrders();
        await loadPositionHistory();
        await loadOrderHistory();
      } else {
        // For unauthenticated users, no data fetching needed
      }
      setIsLoading(false);
    };

    if (!authLoading && !dbLoading) {
      initializeData();
    }
  }, [
    user,
    authLoading,
    dbLoading,
    fetchTransactions,
    fetchAssets,
    fetchPortfolioSnapshots,
    fetchUserStakes,
    fetchActivePositions,
    fetchOpenOrders,
    loadPositionHistory,
    loadOrderHistory,
    isRecoveryLink
  ]);

  // Set up periodic refresh of active positions - only for authenticated users
  useEffect(() => {
    // Only set up the interval if the user is logged in
    if (user) {
      // Clear any existing interval first
      if (positionRefreshIntervalRef.current) {
        clearInterval(positionRefreshIntervalRef.current);
      }
      
      // Set up a new interval to refresh positions every 10 seconds (reduced frequency since WebSocket handles real-time updates)
      positionRefreshIntervalRef.current = window.setInterval(async () => {
        
        try {
          // Refresh futures positions
          await fetchActivePositions();
          
          // Refresh prop firm positions if there's an active challenge
          if (propChallengeAccount) {
            await fetchPropPositions(propChallengeAccount.challengeId);
          }
        } catch (error) {
          console.error('Error refreshing positions:', error);
        }
      }, 10000); // 10 seconds interval (reduced since WebSocket provides real-time updates)
      
      // Clean up the interval when the component unmounts
      return () => {
        if (positionRefreshIntervalRef.current) {
          clearInterval(positionRefreshIntervalRef.current);
          positionRefreshIntervalRef.current = null;
        }
      };
    }
  }, [user, fetchActivePositions, fetchPropPositions, propChallengeAccount]);

  // Control Tawk.to chat widget visibility on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 1024;
    const showChatPages: TradingMode[] = ['home', 'profile'];
    const shouldShowChat = !isMobile || showChatPages.includes(tradingMode);

    const updateChatVisibility = () => {
      if (typeof window !== 'undefined' && (window as any).Tawk_API) {
        if (shouldShowChat) {
          (window as any).Tawk_API.showWidget?.();
        } else {
          (window as any).Tawk_API.hideWidget?.();
        }
      }
    };

    updateChatVisibility();

    const handleResize = () => {
      const nowMobile = window.innerWidth < 1024;
      const nowShouldShow = !nowMobile || showChatPages.includes(tradingMode);
      if (typeof window !== 'undefined' && (window as any).Tawk_API) {
        if (nowShouldShow) {
          (window as any).Tawk_API.showWidget?.();
        } else {
          (window as any).Tawk_API.hideWidget?.();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tradingMode]);

  // Handle futures trade
  const handleFuturesTrade = useCallback(async (
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => {
    try {
      const currentPrice = price ?? getCurrentPrice(symbol || selectedPair);
      if (!Number.isFinite(currentPrice) || currentPrice <= 0) return false;
      await fetchBalances();
      
      // Use the openPosition function from useFuturesTrading hook
      const positionId = await openPosition({
        symbol: symbol || selectedPair,
        side,
        amount,
        leverage,
        marginType,
        orderType: orderType || 'market',
        price: currentPrice,
        stopLoss: stopLoss?.trigger_price,
        takeProfit: takeProfit?.trigger_price,
        contractSize: 1
      });
      
      if (positionId) {
        // Refresh wallet breakdown to update available balance
        refreshBreakdown();

        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handleFuturesTrade:', error);
      return false;
    }
  }, [selectedPair, getCurrentPrice, openPosition, refreshBreakdown, fetchBalances]);

  // Handle closing a futures position
  const handleClosePosition = useCallback(async (positionId: string, livePrice: number) => {
    try {
      const success = await closePosition(positionId, livePrice);

      if (success) {
        refreshBreakdown();
        await loadPositionHistory();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error in handleClosePosition:', error);
      return false;
    }
  }, [closePosition, refreshBreakdown, loadPositionHistory]);

  const handleEditTPSL = useCallback((positionId: string, type: 'takeProfit' | 'stopLoss') => {
    const position = activePositions.find(p => p.id === positionId);
    if (position) {
      setEditingTPSL({ positionId, type, position });
    }
  }, [activePositions]);

  const handleConfirmTPSL = useCallback(async (triggerPrice: number) => {
    if (!editingTPSL) return;

    const updates = editingTPSL.type === 'takeProfit'
      ? { takeProfit: triggerPrice > 0 ? triggerPrice : undefined }
      : { stopLoss: triggerPrice > 0 ? triggerPrice : undefined };

    const success = await updateStopLossTakeProfit(editingTPSL.positionId, updates);
    if (success) {
      setEditingTPSL(null);
    }
  }, [editingTPSL, updateStopLossTakeProfit]);

  // Handle spot order
  const handleSpotOrder = useCallback(async (order: {
    side: 'buy' | 'sell';
    order_type: 'market' | 'limit';
    amount: number;
    price?: number;
    stop_loss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
    take_profit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
  }) => {
    const { side, order_type, amount } = order;
    const price = order.price || currentSelectedPairPrice;
    
    // Calculate total cost/proceeds
    const total = amount * price;
    
    if (side === 'buy') {
      // Check if user has enough USDT
      if (total > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance - total,
        btc_balance: balances.btc_balance + amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: -total,
        description: `Bought ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    } else {
      // Check if user has enough BTC
      if (amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance + total,
        btc_balance: balances.btc_balance - amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: total,
        description: `Sold ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction]);

  // Handle swap between cryptocurrencies
  const handleSwap = useCallback(async (fromSymbol: string, toSymbol: string, fromAmount: number, toAmountReceived?: number) => {
    try {
      // Wait for balances to be fully loaded
      if (dbLoading) {
        throw new Error('Please wait, loading account data...');
      }
      
      // Check available balance for swap
      const swapValueUSD = fromAmount * (fromSymbol === 'USDT' ? 1 : 
                                        fromSymbol === 'BTC' ? currentSelectedPairPrice :
                                        marketData.find(data => data.symbol === `${fromSymbol}USDT`)?.price || 0);
      
      if (swapValueUSD > availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${swapValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
      }
      
      // Validate input parameters
      if (!fromSymbol || !toSymbol || isNaN(fromAmount) || fromAmount <= 0) {
        throw new Error('Invalid swap parameters');
      }

      // Additional security validations
      const MIN_SWAP_AMOUNT = 0.0001;
      const MAX_SWAP_AMOUNT_USD = 1000000;
      const MAX_SLIPPAGE = 0.05; // 5% maximum slippage
      const SWAP_FEE_RATE = 0.001; // 0.1% fee

      // Prevent swapping to the same currency
      if (fromSymbol === toSymbol) {
        throw new Error('Cannot swap to the same currency');
      }

      // Check minimum amount
      if (fromAmount < MIN_SWAP_AMOUNT) {
        throw new Error(`Minimum swap amount is ${MIN_SWAP_AMOUNT}`);
      }

      // Helper to get price for any symbol
      const getPriceForSymbol = (symbol: string): number => {
        if (symbol === 'USDT') return 1;
        // For BTC, use the current selected pair price if it's BTCUSDT, otherwise look up
        if (symbol === 'BTC') return (marketData || []).find(d => d.symbol === 'BTCUSDT')?.price || 0;

        // For other cryptos, look up in marketData
        const usdtPair = `${symbol}USDT`;
        return (marketData || []).find(d => d.symbol === usdtPair)?.price || 0;
      };

      // Get current prices
      const fromPrice = getPriceForSymbol(fromSymbol);
      const toPrice = getPriceForSymbol(toSymbol);

      // Validate prices are available and reasonable
      if (!fromPrice || !toPrice || fromPrice <= 0 || toPrice <= 0) {
        throw new Error('Unable to get current prices for swap');
      }

      // Check maximum USD value
      const fromUsdValue = fromAmount * fromPrice;
      if (fromUsdValue > MAX_SWAP_AMOUNT_USD) {
        throw new Error(`Maximum swap amount is $${MAX_SWAP_AMOUNT_USD.toLocaleString()}`);
      }
      
      // If toAmountReceived is not provided, calculate it
      let calculatedToAmount = toAmountReceived;
      if (!calculatedToAmount || isNaN(calculatedToAmount)) {
        const exchangeRate = fromPrice / toPrice;
        calculatedToAmount = fromAmount * exchangeRate * (1 - SWAP_FEE_RATE);
      } else {
        // If toAmountReceived is provided, validate it against expected calculation
        const expectedExchangeRate = fromPrice / toPrice;
        const expectedToAmount = fromAmount * expectedExchangeRate * (1 - SWAP_FEE_RATE);
        
        // Prevent division by zero or extremely small numbers
        if (expectedToAmount <= 0.00000001) {
          throw new Error('Calculated output amount is too small. Please increase the input amount.');
        }
        
        const slippage = Math.abs(calculatedToAmount - expectedToAmount) / expectedToAmount;
        
        // Additional sanity check: if slippage is unreasonably high, it's likely a calculation error
        if (slippage > 1.0) { // More than 100% slippage indicates a serious error
          throw new Error('Price calculation error detected. Please refresh the page and try again.');
        }
        
        if (slippage > MAX_SLIPPAGE) {
          throw new Error(`Price has changed too much (${(slippage * 100).toFixed(2)}% slippage). Please try again.`);
        }
      }
      
      // Validate calculated amount
      if (!calculatedToAmount || isNaN(calculatedToAmount) || calculatedToAmount <= 0) {
        throw new Error('Invalid amount to receive');
      }
      
      // Additional sanity check: ensure the swap ratio is reasonable

      const feeAmount = fromAmount * SWAP_FEE_RATE;

      // Helper to get current balance of any asset
      const getAssetBalance = (symbol: string): number => {
        if (symbol === 'USDT') return balances.usdt_balance;
        if (symbol === 'BTC') return balances.btc_balance;
        const asset = assets.find(a => a.asset_symbol === symbol);
        return asset ? asset.balance : 0;
      };

      const currentFromBalance = getAssetBalance(fromSymbol);
      
      // Final balance check with buffer for fees
      if (fromAmount > currentFromBalance) {
        throw new Error(`Insufficient ${fromSymbol} balance`);
      }

      // Ensure we're not trying to give out more than what's reasonable
      // Instead of checking numeric ratios, check USD values for reasonableness
      const toUsdValue = calculatedToAmount * toPrice;
      const valueRatio = toUsdValue / fromUsdValue;
      
      // The output USD value should be close to input USD value (accounting for fees)
      // Allow up to 10% difference to account for fees and minor price movements
      if (valueRatio > 1.1 || valueRatio < 0.8) {
        console.error('Swap validation failed:', {
          fromAmount,
          calculatedToAmount,
          fromPrice,
          toPrice,
          fromUsdValue,
          toUsdValue,
          valueRatio,
          expectedRatio: 'should be between 0.8 and 1.1'
        });
        throw new Error(`Swap calculation error: USD value mismatch (ratio: ${valueRatio.toFixed(3)}). Please refresh and try again.`);
      }

      // Log the swap for debugging purposes
      console.log('Executing swap:', {
        fromSymbol,
        toSymbol,
        fromAmount,
        calculatedToAmount,
        fromPrice,
        toPrice,
        exchangeRate: fromPrice / toPrice,
        feeAmount,
        fromBalance: currentFromBalance
      });

      // Deduct from fromSymbol balance
      if (fromSymbol === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - fromAmount });
      } else if (fromSymbol === 'BTC') {
        await updateBalances({ btc_balance: balances.btc_balance - fromAmount });
      } else {
        await updateAssetBalance(fromSymbol, currentFromBalance - fromAmount);
      }

      // Add to toSymbol balance
      if (toSymbol === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance + calculatedToAmount });
      } else if (toSymbol === 'BTC') {
        await updateBalances({
          btc_balance: balances.btc_balance + calculatedToAmount
        });
      } else {
        const toAsset = assets.find(a => a.asset_symbol === toSymbol);
        if (toAsset) {
          await updateAssetBalance(toSymbol, toAsset.balance + calculatedToAmount);
        } else {
          // If the target asset doesn't exist for the user, create it
          // This will insert a new row in user_assets table
          // The updateAssetBalance function already handles this logic
          await updateAssetBalance(toSymbol, calculatedToAmount);
        }
      }
      
      // Add transaction record for swap
      await addTransaction({
        type: 'trade',
        amount: -fromAmount * getPriceForSymbol(fromSymbol), // Negative amount for the source currency
        description: `Swapped ${fromAmount} ${fromSymbol} to ${calculatedToAmount.toFixed(6)} ${toSymbol} (including 0.1% fee)`,
        status: 'completed'
      });
      
      // Refresh wallet breakdown
      refreshBreakdown();

      console.log('Swap completed successfully');
      return true;
    } catch (error: any) {
      console.error('Swap error details:', {
        error,
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction, assets, updateAssetBalance, marketData, dbLoading, availableBalance, refreshBreakdown]);

  // Handle wallet operations (deposit/withdrawal)
  const handleWalletOperation = useCallback(async (type: 'deposit' | 'withdrawal', currency: 'USDT' | 'BTC', amount: number) => {
    if (type === 'deposit') {
      try {
        if (currency === 'USDT') {
          console.log(`Processing USDT deposit of ${amount} for user ${user?.id} (demo: ${user?.user_metadata?.is_demo ? 'yes' : 'no'})`);
          await updateBalances({ usdt_balance: balances.usdt_balance + amount });
        } else {
          await updateBalances({ btc_balance: balances.btc_balance + amount });
        }

        // Add transaction - this will trigger the demo account reset if user is a demo user
        const description = user?.user_metadata?.is_demo
          ? `Initial deposit to convert demo account to live account: ${amount} ${currency}`
          : `Deposited ${amount} ${currency}`;

        const { data: txData, error: txError } = await addTransaction({
          type: 'deposit',
          amount,
          description,
          status: 'completed'
        });

        if (txError) {
          console.error('Error processing deposit transaction:', txError);
          throw new Error(`Failed to process deposit: ${txError.message}`);
        }

        if (currency === 'USDT' && user?.id && txData) {
          try {
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-giveaway-tickets`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                userId: user.id,
                depositAmount: amount,
                transactionId: txData.id || '',
              }),
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success && result.ticketsEarned > 0) {
                console.log(`Earned ${result.ticketsEarned} giveaway tickets!`);
                alert(`Deposit successful! You earned ${result.ticketsEarned} giveaway ticket${result.ticketsEarned > 1 ? 's' : ''}!`);
              }
            }
          } catch (giveawayError) {
            console.error('Error processing giveaway tickets:', giveawayError);
          }
        }
      } catch (error) {
        console.error('Error processing deposit:', error);
        alert('Failed to process deposit. Please try again.');
      }
    } else {
      // Check available balance for withdrawal
      const withdrawalValueUSD = currency === 'USDT' ? amount : amount * currentSelectedPairPrice;
      
      if (withdrawalValueUSD > availableBalance) {
        alert(`Insufficient available balance. Required: ${withdrawalValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
        return;
      }
      
      // Check balance
      if (currency === 'USDT' && amount > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      } else if (currency === 'BTC' && amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      if (currency === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - amount });
      } else {
        await updateBalances({ btc_balance: balances.btc_balance - amount });
      }
      
      // Add transaction
      await addTransaction({
        type: 'withdrawal',
        amount: -amount,
        description: `Withdrawn ${amount} ${currency}`,
        status: 'completed'
      });
      
      // Refresh data after withdrawal
      await fetchBalances();
      await fetchAssets();
      await fetchTransactions();
    }
    
    // Refresh wallet breakdown
    refreshBreakdown();
  }, [balances, updateBalances, addTransaction, fetchBalances, fetchAssets, fetchTransactions, availableBalance, currentSelectedPairPrice, refreshBreakdown]);

  // Handle staking
  const handleStake = useCallback(async (asset: string, amount: number, duration: number) => {
    try {
      // Check available balance for staking
      const stakingValueUSD = asset === 'USDT' ? amount : amount * currentSelectedPairPrice;
      
      if (stakingValueUSD > availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${stakingValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
      }
      
      // Check minimum stake
      if (amount < 0.001) {
        setStakingMessage({
          type: 'error',
          text: `Minimum stake amount is 0.001 ${asset}`
        });
        return;
      }
      
      // Check balance
      if (asset === 'USDT' && amount > balances.usdt_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      } else if (asset === 'BTC' && amount > balances.btc_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      }
      
      // Check if user already has an active stake for this asset
      if (hasActiveStakeForAsset(asset)) {
        throw new Error(`You already have an active stake for ${asset}. Please wait for it to complete before creating a new one.`);
      }
      
      // Calculate APY based on duration
      let apy = 5.0; // Base APY
      if (duration >= 90) apy = 7.5;
      if (duration >= 180) apy = 10.0;
      if (duration >= 365) apy = 12.5;
      
      setIsStaking(true);
      
      // Call the addUserStake function
      const success = await addUserStake(
        asset,
        amount,
        apy,
        duration
      );
      
      if (success) {
        setStakingMessage({
          type: 'success',
          text: `Successfully staked ${amount} ${asset} for ${duration} days`
        });
        
        // Close modal and reset form
        setShowConfirmationModal(false);
        setStakingAmount('');
        setSelectedAsset(null);
        
        // Call the onStake prop to update balances
        await onStake(asset, amount, duration);
        
        // Refresh user stakes
        await fetchUserStakes();
        
        // Refresh wallet breakdown
        refreshBreakdown();
      } else {
        throw new Error('Staking failed');
      }
    } catch (error: any) {
      setStakingMessage({
        type: 'error',
        text: error.message || 'Failed to stake. Please try again.'
      });
    } finally {
      setIsStaking(false);
    }
  }, [balances, updateBalances, addTransaction, addUserStake, fetchUserStakes, availableBalance, currentSelectedPairPrice, refreshBreakdown]);

  // Calculate time remaining for a stake
  // Show loading state while checking for existing challenge
  if (propFirmLoading && propChallengeAccount === null) {
    return <LoadingScreen message="Loading prop firm challenge data..." />;
  }

  return (
      <div className="min-h-screen text-white">
          {user && <UserHeartbeat />}
          <Routes>
            {/* Handle password recovery at root path */}
            <Route path="/" element={
              isRecoveryLink ? (
                <ResetPasswordPage />
              ) : authLoading ? (
                null
              ) : user ? (
                <>
                  <Header
                    tradingMode={tradingMode}
                    selectedPair={selectedPair}
                    setSelectedPair={setSelectedPair}
                    currentPrice={currentSelectedPairPrice}
                    marketData={(marketData || []).find(data => data.symbol === selectedPair)}
                    usdtBalance={balances.usdt_balance}
                    btcBalance={balances.btc_balance}
                    totalPortfolioValue={totalPortfolioValue}
                    user={user}
                    signOut={signOut}
                    marketDataList={marketData}
                    userStatus={userStatus}
                    isDemoAccount={isDemoAccount}
                    selectedDisplayCurrency={selectedDisplayCurrency}
                    setSelectedDisplayCurrency={handleDisplayCurrencyChange}
                  />

                  <main className="lg:ml-16">
                    <Outlet />
                  </main>
                </>
              ) : (
                <Navigate to="/signin" replace />
              )
            }>
              <Route index element={
                      <HomePage 
                        currentBtcPrice={currentSelectedPairPrice}
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        marketData={marketData}
                        futuresPositions={activePositions}
                        transactions={transactions}
                        newsItems={newsItems}
                      />
              } />

              <Route path="swap" element={
                      <SwapCryptoPage
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        currentBtcPrice={currentSelectedPairPrice}
                        onSwap={handleSwap}
                        userAssets={assets}
                      />
              } />

              <Route path="futures" element={
                      <>
                        <div className="hidden lg:block h-full">
                          <TradingTerminalLayout
                            header={
                              <TradingPairHeader
                                selectedPair={selectedPair}
                                tradingMode="futures"
                                onPairChange={setSelectedPair}
                              />
                            }
                            leftPanel={
                              <OrderBookWithTrades
                                selectedPair={selectedPair}
                                tradingMode="futures"
                              />
                            }
                            chartToolbar={
                              <ChartToolbar />
                            }
                            chart={
                              <TradingChart key={selectedPair} selectedPair={selectedPair} />
                            }
                            rightPanel={
                              <UnifiedTradingPanel
                                selectedPair={selectedPair}
                                usdtBalance={balances.usdt_balance}
                                availableBalance={actualAvailableBalance}
                                maxAllowedLeverage={currentMaxFuturesLeverage}
                                minAllowedLeverage={currentMinFuturesLeverage}
                                calculateLiquidationPrice={calculateLiquidationPrice}
                                onTrade={handleFuturesTrade}
                              />
                            }
                            bottomPanel={
                              <PositionsPanel
                                positions={activePositions
                                  .filter(pos => isCryptoSymbol(pos.symbol))
                                  .map(pos => ({
                                    ...pos,
                                    entryPrice: pos.entryPrice || pos.entry_price || 0,
                                    currentPrice: pos.currentPrice || pos.current_price || 0,
                                    liquidationPrice: pos.liquidationPrice || pos.liquidation_price || 0,
                                    accumulated_swap_cost: pos.accumulated_swap_cost || pos.accumulatedSwapCost || 0,
                                    takeProfit: pos.takeProfit || pos.tp_price || null,
                                    stopLoss: pos.stopLoss || pos.sl_price || null
                                  }))}
                                openOrders={openOrders
                                  .filter(order => isCryptoSymbol(order.symbol))
                                  .map(order => ({
                                    ...order,
                                    created_at: order.createdAt || order.created_at
                                  }))}
                                positionHistory={positionHistory
                                  .filter(h => isCryptoSymbol(h.symbol))
                                  .map(h => ({
                                    id: h.id,
                                    symbol: h.symbol,
                                    side: h.side,
                                    entryPrice: h.entryPrice,
                                    exitPrice: h.exitPrice,
                                    amount: h.amount,
                                    pnl: h.pnl,
                                    closeTime: h.closeTime,
                                    durationSeconds: h.durationSeconds,
                                    accumulatedSwapCost: h.accumulatedSwapCost
                                  }))}
                                orderHistory={orderHistory.filter(o => isCryptoSymbol(o.symbol))}
                                onClosePosition={handleClosePosition}
                                onCancelOrder={cancelOrder}
                                onCancelAllOrders={cancelAllOpenOrders}
                                onEditTPSL={handleEditTPSL}
                              />
                            }
                          />
                        </div>
                        <div className="lg:hidden h-full">
                          <MobileTradingLayout
                            selectedPair={selectedPair}
                            tradingMode="futures"
                            onPairChange={setSelectedPair}
                            usdtBalance={balances.usdt_balance}
                            availableBalance={actualAvailableBalance}
                            maxAllowedLeverage={currentMaxFuturesLeverage}
                            minAllowedLeverage={currentMinFuturesLeverage}
                            calculateLiquidationPrice={calculateLiquidationPrice}
                            onTrade={handleFuturesTrade}
                            positions={activePositions
                              .filter(pos => isCryptoSymbol(pos.symbol))
                              .map(pos => ({
                                ...pos,
                                entryPrice: pos.entryPrice || pos.entry_price || 0,
                                currentPrice: pos.currentPrice || pos.current_price || 0,
                                liquidationPrice: pos.liquidationPrice || pos.liquidation_price || 0,
                                accumulated_swap_cost: pos.accumulated_swap_cost || pos.accumulatedSwapCost || 0,
                                takeProfit: pos.takeProfit || pos.tp_price || null,
                                stopLoss: pos.stopLoss || pos.sl_price || null
                              }))}
                            openOrders={openOrders
                              .filter(order => isCryptoSymbol(order.symbol))
                              .map(order => ({
                                ...order,
                                created_at: order.createdAt || order.created_at
                              }))}
                            positionHistory={positionHistory
                              .filter(h => isCryptoSymbol(h.symbol))
                              .map(h => ({
                                id: h.id,
                                symbol: h.symbol,
                                side: h.side,
                                entryPrice: h.entryPrice,
                                exitPrice: h.exitPrice,
                                amount: h.amount,
                                pnl: h.pnl,
                                closeTime: h.closeTime,
                                durationSeconds: h.durationSeconds,
                                accumulatedSwapCost: h.accumulatedSwapCost
                              }))}
                            orderHistory={orderHistory.filter(o => isCryptoSymbol(o.symbol))}
                            onClosePosition={handleClosePosition}
                            onCancelOrder={cancelOrder}
                            onCancelAllOrders={cancelAllOpenOrders}
                            onEditTPSL={handleEditTPSL}
                          />
                        </div>
                      </>
              } />

              <Route path="cfd" element={
                      <>
                        <div className="hidden lg:block h-full">
                          <TradingTerminalLayout
                            header={
                              <TradingPairHeader
                                selectedPair={selectedPair}
                                tradingMode="cfd"
                                onPairChange={setSelectedPair}
                              />
                            }
                            leftPanel={
                              <OrderBookWithTrades
                                selectedPair={selectedPair}
                                tradingMode="cfd"
                              />
                            }
                            chartToolbar={
                              <ChartToolbar />
                            }
                            chart={
                              <TradingChart key={selectedPair} selectedPair={selectedPair} />
                            }
                            rightPanel={
                              <UnifiedTradingPanel
                                selectedPair={selectedPair}
                                usdtBalance={balances.usdt_balance}
                                availableBalance={actualAvailableBalance}
                                maxAllowedLeverage={currentCfdMaxLeverage}
                                minAllowedLeverage={currentCfdMinLeverage}
                                calculateLiquidationPrice={calculateLiquidationPrice}
                                onTrade={handleFuturesTrade}
                              />
                            }
                            bottomPanel={
                              <PositionsPanel
                                positions={activePositions
                                  .filter(pos => isCfdSymbol(pos.symbol))
                                  .map(pos => ({
                                    ...pos,
                                    entryPrice: pos.entryPrice || pos.entry_price || 0,
                                    currentPrice: pos.currentPrice || pos.current_price || 0,
                                    liquidationPrice: pos.liquidationPrice || pos.liquidation_price || 0,
                                    accumulated_swap_cost: pos.accumulated_swap_cost || pos.accumulatedSwapCost || 0,
                                    takeProfit: pos.takeProfit || pos.tp_price || null,
                                    stopLoss: pos.stopLoss || pos.sl_price || null
                                  }))}
                                openOrders={openOrders
                                  .filter(order => isCfdSymbol(order.symbol))
                                  .map(order => ({
                                    ...order,
                                    created_at: order.createdAt || order.created_at
                                  }))}
                                positionHistory={positionHistory
                                  .filter(h => isCfdSymbol(h.symbol))
                                  .map(h => ({
                                    id: h.id,
                                    symbol: h.symbol,
                                    side: h.side,
                                    entryPrice: h.entryPrice,
                                    exitPrice: h.exitPrice,
                                    amount: h.amount,
                                    pnl: h.pnl,
                                    closeTime: h.closeTime,
                                    durationSeconds: h.durationSeconds,
                                    accumulatedSwapCost: h.accumulatedSwapCost
                                  }))}
                                orderHistory={orderHistory.filter(o => isCfdSymbol(o.symbol))}
                                onClosePosition={handleClosePosition}
                                onCancelOrder={cancelOrder}
                                onCancelAllOrders={cancelAllOpenOrders}
                                onEditTPSL={handleEditTPSL}
                              />
                            }
                          />
                        </div>
                        <div className="lg:hidden h-full">
                          <MobileTradingLayout
                            selectedPair={selectedPair}
                            tradingMode="cfd"
                            onPairChange={setSelectedPair}
                            usdtBalance={balances.usdt_balance}
                            availableBalance={actualAvailableBalance}
                            maxAllowedLeverage={currentCfdMaxLeverage}
                            minAllowedLeverage={currentCfdMinLeverage}
                            calculateLiquidationPrice={calculateLiquidationPrice}
                            onTrade={handleFuturesTrade}
                            positions={activePositions
                              .filter(pos => isCfdSymbol(pos.symbol))
                              .map(pos => ({
                                ...pos,
                                entryPrice: pos.entryPrice || pos.entry_price || 0,
                                currentPrice: pos.currentPrice || pos.current_price || 0,
                                liquidationPrice: pos.liquidationPrice || pos.liquidation_price || 0,
                                accumulated_swap_cost: pos.accumulated_swap_cost || pos.accumulatedSwapCost || 0,
                                takeProfit: pos.takeProfit || pos.tp_price || null,
                                stopLoss: pos.stopLoss || pos.sl_price || null
                              }))}
                            openOrders={openOrders
                              .filter(order => isCfdSymbol(order.symbol))
                              .map(order => ({
                                ...order,
                                created_at: order.createdAt || order.created_at
                              }))}
                            positionHistory={positionHistory
                              .filter(h => isCfdSymbol(h.symbol))
                              .map(h => ({
                                id: h.id,
                                symbol: h.symbol,
                                side: h.side,
                                entryPrice: h.entryPrice,
                                exitPrice: h.exitPrice,
                                amount: h.amount,
                                pnl: h.pnl,
                                closeTime: h.closeTime,
                                durationSeconds: h.durationSeconds,
                                accumulatedSwapCost: h.accumulatedSwapCost
                              }))}
                            orderHistory={orderHistory.filter(o => isCfdSymbol(o.symbol))}
                            onClosePosition={handleClosePosition}
                            onCancelOrder={cancelOrder}
                            onCancelAllOrders={cancelAllOpenOrders}
                            onEditTPSL={handleEditTPSL}
                          />
                        </div>
                      </>
              } />

              <Route path="exchanges" element={
                      <ExchangesTradingPage
                        selectedPair={selectedPair}
                        onPairChange={setSelectedPair}
                      />
              } />

              <Route path="exchange-arbitrage" element={
                      <ExchangesArbitragePage />
              } />

              <Route path="prop-firm" element={
                      <PropFirmChallengePage
                        currentPrice={currentSelectedPairPrice}
                        usdtBalance={balances.usdt_balance}
                        selectedPair={selectedPair}
                        setSelectedPair={setSelectedPair}
                        robotState={robotState}
                        updateBalances={updateBalances}
                        addTransaction={addTransaction}
                        updateRobotState={updateRobotState}
                        onClosePropPosition={async (positionId: string) => {
                          try {
                            const success = await closePropPosition(positionId, selectedChallenge?.id || '');
                            if (success) {
                              refreshBreakdown();
                            }
                            return success;
                          } catch (error) {
                            console.error('Error closing prop position:', error);
                            return false;
                          }
                        }}
                        onCancelPropOrder={async (orderId: string) => {
                          try {
                            const success = await cancelPropOrder(orderId, selectedChallenge?.id || '');
                            return success;
                          } catch (error) {
                            console.error('Error cancelling prop order:', error);
                            return false;
                          }
                        }}
                        onCancelAllPropOrders={async () => {
                          try {
                            const success = await cancelAllPropOrders(selectedChallenge?.id || '');
                            return success;
                          } catch (error) {
                            console.error('Error cancelling all prop orders:', error);
                            return false;
                          }
                        }}
                      />
              } />

              <Route path="robot" element={
                      <ArbitrageRobotPage
                        robotState={robotState}
                        updateRobotState={updateRobotState}
                        selectedPair={selectedPair}
                        setSelectedPair={setSelectedPair}
                        usdtBalance={balances.usdt_balance}
                        updateBalances={updateBalances}
                        addTransaction={addTransaction}
                        marketData={marketData}
                        usedMargin={usedMargin}
                        availableBalance={availableBalance}
                      />
              } />

              <Route path="events" element={
                      <PolymarketEventBetting
                        usdtBalance={balances.usdt_balance}
                        userId={user?.id ?? ''}
                        onBalanceRefresh={fetchBalances}
                      />
              } />

              <Route path="wallet" element={
                      <WalletPage
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        currentBtcPrice={currentSelectedPairPrice}
                        onWalletOperation={handleWalletOperation}
                        transactions={transactions}
                        fetchTransactions={fetchTransactions}
                        addTransaction={addTransaction}
                        updateBalances={updateBalances}
                        kycStatus={kycStatus}
                        totalPortfolioValue={totalPortfolioValue}
                        userAssets={assets}
                        marketData={marketData}
                        walletBreakdown={{
                          totalBalance,
                          usedMargin,
                          unrealizedPnl,
                          availableBalance,
                          reserved
                        }}
                      />
              } />

              <Route path="profile" element={
                      <ProfilePage
                        user={user}
                        signOut={signOut}
                        kycStatus={kycStatus}
                        updateKycStatus={updateKycStatus}
                        referralCode={referralCode}
                        referralCount={referralCount}
                        referredUsers={referredUsers}
                        portfolioSnapshots={portfolioSnapshots}
                        createPortfolioSnapshot={createPortfolioSnapshot}
                        totalPortfolioValue={totalPortfolioValue}
                        totalPositionsPnl={unrealizedPnl}
                        userStatus={userStatus}
                        onUpdatePassword={handleUpdatePassword}
                        selectedDisplayCurrency={selectedDisplayCurrency}
                      />
              } />

              <Route path="staking" element={
                      <StakingPage
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        currentBtcPrice={currentSelectedPairPrice}
                        onStake={handleStake}
                        userStakes={userStakes}
                        calculateCurrentEarnings={calculateCurrentEarnings}
                        cancelUserStake={cancelUserStake}
                        availableBalance={actualAvailableBalance}
                        updateBalances={updateBalances}
                        addTransaction={addTransaction}
                        fetchUserStakes={fetchUserStakes}
                        refreshBreakdown={refreshBreakdown}
                      />
              } />

              <Route path="wheel" element={
                      <SpinTheWheel />
              } />

              <Route path="payment-sandbox" element={
                      <PaymentSandbox />
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            
            {/* Auth routes */}
            <Route path="/signin" element={
              authLoading ? null : user ? <Navigate to="/" replace /> : <SignInPage />
            } />
            <Route path="/signup" element={
              authLoading ? null : user ? <Navigate to="/" replace /> : <SignUpPage />
            } />
            <Route path="/sign-up" element={
              authLoading ? null : user ? <Navigate to="/" replace /> : <SignUpPage />
            } />
            <Route path="/forgot-password" element={
              authLoading ? null : user ? <Navigate to="/" replace /> : <ForgotPasswordPage />
            } />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/trading-fees" element={<TradingFeesPage />} />

            {/* Test routes */}
            <Route path="/test/finnhub" element={<FinnhubWebSocketTest />} />
            <Route path="/test/stocks" element={<StockIndexTestPage />} />
            <Route path="/test/valore" element={<ValoreTestPage />} />
          </Routes>
          
          {/* Auth Modal */}
          {showAuthModal && (
            <AuthModal onClose={() => setShowAuthModal(false)} />
          )}

          {/* TP/SL Edit Modal */}
          {editingTPSL && (
            <TakeProfitStopLossModal
              isOpen={true}
              onClose={() => setEditingTPSL(null)}
              type={editingTPSL.type}
              side={editingTPSL.position.side || 'long'}
              entryPrice={editingTPSL.position.entryPrice || editingTPSL.position.entry_price || 0}
              amount={editingTPSL.position.amount || 0}
              leverage={editingTPSL.position.leverage || 1}
              onConfirm={(triggerPrice) => {
                handleConfirmTPSL(triggerPrice);
              }}
            />
          )}

          {/* Market Loading Screen */}
          {isPreparingMarkets && (
            <MarketLoadingScreen onComplete={() => setIsPreparingMarkets(false)} />
          )}

          {/* Loading Screen */}
          {(authLoading || isLoading) && !isRecoveryLink && !isPreparingMarkets && (
            <LoadingScreen />
          )}
        </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/about" element={<AboutPage />} />
        <Route path="*" element={
          <MarketDataProvider>
            <ValorePriceProvider>
              <BybitPriceProvider>
                <UnifiedPriceProvider>
                  <AppContent />
                </UnifiedPriceProvider>
              </BybitPriceProvider>
            </ValorePriceProvider>
          </MarketDataProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
